import React, { useState } from 'react';
import { authApi } from '../../services/authApi';
import { useAuth } from '../../auth/AuthContext';

interface Props {
  onSuccess: () => void;
  onBack: () => void;
}

export const OTP: React.FC<Props> = ({ onSuccess, onBack }) => {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    // Local dev bypass
    if (process.env.NODE_ENV === 'development' && otp === '111111') {
      localStorage.removeItem('dd_session');
      setUser({ token: 'dev-token', type: 1, firstName: 'Dev', lastName: 'User' });
      onSuccess();
      return;
    }

    const sessionToken = localStorage.getItem('dd_session');
    if (!sessionToken) { onBack(); return; }

    setLoading(true);
    try {
      const loginRes = await authApi.verifyOtp(otp, sessionToken);
      const accessToken = loginRes.data?.accessToken;
      if (!accessToken) throw new Error('No access token');

      const userRes = await authApi.getUserInfo(accessToken);
      const info = userRes.data;

      if (info.type !== 1) {
        setError('Access denied. This dashboard is for Payro admins only.');
        setLoading(false);
        return;
      }

      localStorage.removeItem('dd_session');
      setUser({
        token: accessToken,
        type: info.type,
        firstName: info.firstName,
        lastName: info.lastName,
      });
      onSuccess();
    } catch (err: any) {
      if (err.response?.status === 401) setError('Invalid code. Please try again.');
      else setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>Payro</div>
        <h1 style={styles.title}>Verify your number</h1>
        <p style={styles.subtitle}>Enter the 6-digit code sent to your phone</p>
        <form onSubmit={handleSubmit}>
          <input
            style={styles.input}
            type="text"
            placeholder="000000"
            value={otp}
            onChange={e => { setOtp(e.target.value); setError(''); }}
            maxLength={6}
            autoFocus
          />
          {error && <p style={styles.error}>{error}</p>}
          <button style={styles.button} type="submit" disabled={loading}>
            {loading ? 'Verifying...' : 'Sign In'}
          </button>
        </form>
        <button style={styles.back} onClick={onBack}>← Back</button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '48px 40px',
    width: 380,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    textAlign: 'center',
  },
  logo: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 2,
    color: '#888',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1a1a2e',
    marginBottom: 8,
  },
  subtitle: {
    color: '#888',
    fontSize: 14,
    marginBottom: 32,
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: 24,
    letterSpacing: 8,
    textAlign: 'center',
    border: '1.5px solid #e0e0e0',
    borderRadius: 8,
    outline: 'none',
    marginBottom: 16,
    boxSizing: 'border-box',
  },
  button: {
    width: '100%',
    padding: '14px',
    background: '#1a1a2e',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
  },
  error: {
    color: '#e53e3e',
    fontSize: 13,
    marginBottom: 8,
    textAlign: 'left',
  },
  back: {
    marginTop: 20,
    background: 'none',
    border: 'none',
    color: '#888',
    cursor: 'pointer',
    fontSize: 14,
  },
};
